import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Bundle tier selector with per-pair variant choice.
 *
 * Each tier declares how many units it adds. A tier can either reuse the main
 * variant picker's selection (quantity mode) or expose one row of option
 * selects per unit, letting the shopper mix shades and sizes across the bundle.
 *
 * Horizon's product form posts a single variant, so when a tier is in per-pair
 * mode we intercept the add-to-cart click and POST every line to the AJAX cart
 * ourselves, then dispatch Shopify's standard cart event so the cart icon and
 * drawer update exactly as they would for a native add.
 */
class FsBundleTiers extends HTMLElement {
  connectedCallback() {
    this.variants = this.#readVariants();
    this.tiers = Array.from(this.querySelectorAll('.fs-tier-wrap'));
    this.status = this.querySelector('[data-fs-status]');

    this.tiers.forEach((wrap) => {
      wrap.querySelector('.fs-tier')?.addEventListener('click', () => this.select(wrap));
      wrap.querySelectorAll('.fs-pair').forEach((pair) => {
        pair.querySelectorAll('select[data-opt]').forEach((sel) => {
          sel.addEventListener('change', () => {
            sel.removeAttribute('aria-invalid');
            this.refresh();
          });
        });
        pair.querySelectorAll('.fs-sw').forEach((sw) => {
          sw.addEventListener('click', () => this.#pickSwatch(pair, sw));
        });
      });
    });

    const initial =
      this.tiers.find((t) => t.dataset.preselected === 'true') || this.tiers[0];
    if (initial) this.select(initial, true);

    // Capture phase so we run before Horizon's own add-to-cart handler.
    this.onCaptureClick = (event) => this.#interceptAddToCart(event);
    document.addEventListener('click', this.onCaptureClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onCaptureClick, true);
    this.observer?.disconnect();
  }

  #readVariants() {
    try {
      return JSON.parse(this.querySelector('[data-fs-variants]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  get activeTier() {
    return this.tiers.find((t) => t.querySelector('.fs-tier')?.getAttribute('aria-pressed') === 'true');
  }

  get form() {
    return (
      this.closest('form[action*="/cart/add"]') ||
      this.closest('.shopify-section')?.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  select(wrap, initial) {
    this.tiers.forEach((t) => {
      const on = t === wrap;
      t.querySelector('.fs-tier')?.setAttribute('aria-pressed', String(on));
      const pairs = t.querySelector('.fs-pairs');
      if (pairs) pairs.hidden = !on;
    });
    this.refresh(initial);
  }

  /** Units for the active tier, as arrays of option values. */
  #selections() {
    const wrap = this.activeTier;
    if (!wrap) return [];
    const qty = parseInt(wrap.dataset.qty || '1', 10);
    const pairs = Array.from(wrap.querySelectorAll('.fs-pair'));

    if (!pairs.length) {
      // Quantity mode: repeat whatever the main variant picker has selected.
      const current = this.#currentPickerOptions();
      return current ? Array.from({ length: qty }, () => current) : [];
    }
    return pairs.map((pair) =>
      Array.from(pair.querySelectorAll('[data-opt]'))
        .sort((a, b) => Number(a.dataset.opt) - Number(b.dataset.opt))
        .map((control) => control.value)
    );
  }

  #currentPickerOptions() {
    const picker = document.querySelector('variant-picker');
    if (!picker) return null;
    const checked = Array.from(picker.querySelectorAll('input[type="radio"]:checked'));
    if (checked.length) return checked.map((i) => i.value);
    const selects = Array.from(picker.querySelectorAll('select'));
    return selects.length ? selects.map((s) => s.value) : null;
  }

  /** Selecting a swatch updates its hidden input, its label, and the gallery. */
  #pickSwatch(pair, sw) {
    const col = sw.closest('.fs-pair__col');
    col?.querySelectorAll('.fs-sw').forEach((b) => b.setAttribute('aria-pressed', String(b === sw)));

    const input = col?.querySelector('input[data-opt]');
    if (input) input.value = sw.dataset.value;

    const label = col?.querySelector('[data-fs-value]');
    if (label) label.textContent = sw.dataset.value;

    if (input) this.#showMediaFor(Number(input.dataset.opt), sw.dataset.value);
    this.refresh();
  }

  /** Jumps the product gallery to the first image belonging to this option value. */
  #showMediaFor(optionIndex, value) {
    const match = this.variants.find((v) => v.o[optionIndex] === value && v.m);
    if (!match) return;

    const gallery = document.querySelector('media-gallery');
    const slideshow = gallery?.slideshow;
    if (!slideshow) return;

    const slides = slideshow.refs?.slides || Array.from(slideshow.querySelectorAll('slideshow-slide'));
    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${match.m}"]`));
    if (index >= 0) slideshow.select(index, undefined, { animate: true });
  }

  #match(options) {
    return this.variants.find(
      (v) => v.o.length === options.length && v.o.every((val, i) => val === options[i])
    );
  }

  /** Resolves selections to cart lines, reporting the first unavailable pick.
   *
   * Rows that land on the same variant are merged into one line. Shopify does
   * that merge server-side anyway, so posting N identical ids comes back as one
   * item and its own analytics listener then complains that the payload and the
   * response have different numbers of items.
   */
  resolve() {
    const byVariant = new Map();
    const problems = [];
    this.#selections().forEach((opts, i) => {
      if (opts.some((v) => !v)) {
        problems.push({ i, opts, reason: 'unchosen' });
        return;
      }
      const variant = this.#match(opts);
      if (!variant) problems.push({ i, opts, reason: 'unavailable' });
      else if (!variant.a) problems.push({ i, opts, reason: 'sold-out' });
      else byVariant.set(variant.id, (byVariant.get(variant.id) || 0) + 1);
    });
    const lines = Array.from(byVariant, ([id, quantity]) => ({ id, quantity }));
    return { lines, problems };
  }

  refresh(initial) {
    const wrap = this.activeTier;
    if (!wrap) return;

    // Quantity mode still drives Horizon's own quantity input.
    const pairs = wrap.querySelectorAll('.fs-pair').length;
    if (!pairs) this.#setNativeQuantity(wrap.dataset.qty);
    else this.#setNativeQuantity(1);

    this.dataset.mode = pairs ? 'pairs' : 'quantity';
    // The sticky bar is not a sibling of this element, so a root-level flag is
    // the only reliable hook for hiding its single-variant title and price.
    document.documentElement.classList.toggle('fs-bundle-pairs', pairs > 0);
    // The sticky-bar rework is opt-in per block, so a plain PDP keeps Horizon's own bar.
    document.documentElement.classList.toggle(
      'fs-bundle-sticky-total',
      pairs > 0 && this.dataset.stickyTotal === 'true'
    );
    this.dataset.units = wrap.dataset.qty || '1';

    this.total = wrap.dataset.total;
    if (this.dataset.suffixButton === 'true') this.#paintButton(wrap.dataset.total);
    else this.#paintSticky();

    const { problems } = this.resolve();
    // On load an unchosen size is the expected state, not an error to shout about.
    this.#reportProblems(problems.filter((p) => p.reason !== 'unchosen'));
    if (!initial) this.dispatchEvent(new CustomEvent('fs:tier-change', { bubbles: true }));
  }

  #setNativeQuantity(qty) {
    const input = this.form?.querySelector('input[name="quantity"]');
    if (!input) return;
    input.value = String(qty);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #reportProblems(problems) {
    if (!this.status) return;
    if (!problems.length) {
      this.status.textContent = '';
      this.status.hidden = true;
      return;
    }
    const word = this.dataset.pairWord || 'Pair';
    const unchosen = problems.filter((p) => p.reason === 'unchosen');
    const soldOut = problems.filter((p) => p.reason === 'sold-out');
    const missing = problems.filter((p) => p.reason === 'unavailable');
    const label = (list) => list.map((p) => `${word} ${p.i + 1}`).join(', ');
    const parts = [];
    if (unchosen.length) parts.push(`${label(unchosen)}: ${this.dataset.unchosenText || 'choose a size'}`);
    if (soldOut.length) parts.push(`${label(soldOut)}: ${this.dataset.soldOutText || 'sold out right now'}`);
    if (missing.length) {
      parts.push(`${label(missing)}: ${this.dataset.unavailableText || 'that combination is unavailable'}`);
    }
    this.status.textContent = parts.join(' · ');
    this.status.hidden = false;
  }

  /** A blocked add must be obvious, or the button just looks broken. */
  #flagProblems(problems) {
    const wrap = this.activeTier;
    if (!wrap) return;
    const rows = Array.from(wrap.querySelectorAll('.fs-pair'));

    rows.forEach((row) => {
      row.querySelectorAll('[data-opt]').forEach((c) => c.removeAttribute('aria-invalid'));
    });

    let firstControl = null;
    problems.forEach((p) => {
      const row = rows[p.i];
      if (!row) return;
      row.querySelectorAll('select[data-opt]').forEach((c) => {
        if (p.reason !== 'unchosen' || !c.value) {
          c.setAttribute('aria-invalid', 'true');
          if (!firstControl) firstControl = c;
        }
      });
    });

    if (!firstControl) return;
    try {
      firstControl.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } catch (e) {
      firstControl.scrollIntoView();
    }
    setTimeout(() => firstControl.focus({ preventScroll: true }), 250);
  }

  /** Horizon re-renders the button on variant change, so re-apply on mutation. */
  #paintButton(total) {
    const form = this.form;
    if (!form) return;
    this.total = total;
    const paint = () => {
      document.querySelectorAll('.add-to-cart-button').forEach((btn) => {
        let span =
          btn.querySelector('.add-to-cart-text__content > span:first-child') || btn.querySelector('.fs-atc-label');
        if (!span) {
          // Horizon's standalone button keeps its label as a bare text node, so
          // wrap it once to give the price suffix somewhere to live.
          const node = [...btn.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
          if (!node) return;
          span = document.createElement('span');
          span.className = 'fs-atc-label';
          span.textContent = node.textContent.trim();
          node.replaceWith(span);
        }
        if (!span.dataset.fsBase) span.dataset.fsBase = span.textContent.trim();
        // A sold-out or unavailable label should not carry a price.
        // The sticky bar shows the price beside the button, so its label stays a
        // plain call to action rather than repeating the total.
        const inSticky = this.dataset.stickyTotal === 'true' && !!btn.closest('sticky-add-to-cart');
        const suffix = btn.disabled || inSticky ? '' : ` – ${this.total}`;
        const next = span.dataset.fsBase + suffix;
        if (span.textContent.trim() !== next) span.textContent = next;
      });
      this.#paintSticky();
    };
    paint();
    if (!this.observer) {
      this.observer = new MutationObserver(paint);
      this.observer.observe(form, { childList: true, subtree: true });
    }
  }

  /** Horizon's sticky bar prices a single unit; a bundle needs its own total. */
  #paintSticky() {
    if (this.dataset.stickyTotal !== 'true') return;
    const price = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__price');
    if (!price || this.dataset.mode !== 'pairs') return;
    const wrap = this.activeTier;
    const name = wrap?.querySelector('.fs-tier__name')?.firstChild?.textContent.trim() || '';
    // Total and offer name sit on two lines so the bar keeps room for the button.
    // Horizon rewrites this node's text on its own updates, so compare text, not markup.
    if (price.textContent.trim() === `${this.total}${name}`) return;
    const total = document.createElement('span');
    total.className = 'fs-sticky-total';
    total.textContent = this.total;
    price.replaceChildren(total);
    if (name) {
      const offer = document.createElement('span');
      offer.className = 'fs-sticky-offer';
      offer.textContent = name;
      price.append(offer);
    }
  }

  #interceptAddToCart(event) {
    const button = event.target instanceof Element ? event.target.closest('.add-to-cart-button') : null;
    if (!button || this.dataset.mode !== 'pairs') return;

    // Only claim buttons belonging to this product's own section. Without this a
    // quick-add on a related-products carousel, or a second product on the page,
    // would be hijacked by whichever bundle happened to be in pairs mode.
    const scope = this.closest('.shopify-section');
    if (scope && !scope.contains(button)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.addBundle(button);
  }

  async addBundle(button) {
    // Horizon's sticky add-to-cart bar forwards its own click to the main
    // button, so a single user action raises two separate click events and this
    // would run twice, doubling the bundle. stopImmediatePropagation cannot
    // help -- they are two events, not two listeners on one event. An in-flight
    // guard also absorbs impatient double-clicks.
    if (this.adding) return;
    this.adding = true;

    const { lines, problems } = this.resolve();
    if (problems.length || !lines.length) {
      this.#reportProblems(problems);
      this.#flagProblems(problems);
      this.adding = false;
      return;
    }

    button?.setAttribute('aria-busy', 'true');

    // Horizon's cart drawer and cart items morph themselves from section HTML
    // returned by the add request, so we have to ask for the same sections its
    // own product form asks for -- otherwise there is nothing to render.
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.push(node.dataset.sectionId);
    });

    const totalQuantity = lines.reduce((n, l) => n + l.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items: lines.map((l) => ({ id: Number(l.id), quantity: l.quantity })),
          sections: sectionIds.join(','),
        }),
      });
      const body = await res.json();
      if (!res.ok || body.status) {
        throw new Error(body?.description || body?.message || 'Add to cart failed');
      }

      const cartUrl = window.Theme?.routes?.cart_url || '/cart';
      const cart = await fetch(`${cartUrl}.js`).then((r) => r.json());

      // The event contract wants a Storefront-shaped cart, not the AJAX one:
      // standard-events destructures `lines` off it and maps over them. Handing
      // it the raw /cart.js body (which has `items`, not `lines`) throws inside
      // Shopify's own listener, which rejects the promise for every cart
      // subscriber -- icon, drawer and items all silently stop updating.
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'fs-bundle-tiers',
          sourceId: this.id || 'fs-bundle-tiers',
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          sections: body.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (this.status) {
        this.status.textContent = error.message;
        this.status.hidden = false;
      }
    } finally {
      this.adding = false;
      button?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('fs-bundle-tiers')) {
  customElements.define('fs-bundle-tiers', FsBundleTiers);
}
